import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { VStack, HStack, Section } from 'tio-ui/components/layout';
import { Button } from 'tio-ui/components/buttons';
import { Select } from 'tio-ui/components/forms';
import Table from 'tio-ui/components/table';
import NativeChart from 'tio-reimagined/components/chart';
import type StoreService from '@ember-data/store';
import { not, eq } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import {
  getModelTypeFromDataset,
  AVAILABLE_DATASETS,
  TIME_RANGE_OPTIONS,
} from '../utils/dataset-utils';
import { ChartDataTransformer } from '../utils/chart-data-transformer';
import { OpenSearchFieldMapper } from '../utils/opensearch-field-mapper';
import { DateGroupingUtility } from '../utils/date-grouping';
import type { ChartDataPoint, ChartType } from 'tio-common/types/chart';
import type { FieldOption, OpenSearchMapping } from '../utils/opensearch-field-mapper';
import type { DateGroupingInterval } from '../utils/date-grouping';
import type Report from '../models/reports';
import { ReportLoader, type ReportLoadContext } from '../utils/report-loader';
import { snakeToCamelCase, camelToSnakeCase } from 'tio-common/utils/string-case-conversion';

interface ChartingSignature {
  Args: {
    // Data input
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openSearchResponse?: any; // OpenSearchResponse data to display
    selectedField: string;
    chartType: ChartType;
    dateGrouping: DateGroupingInterval;
    showDataTable: boolean;
    selectedDataset: string;
    selectedTimeRange: string;
    sumField?: string; // Optional field to sum instead of counting
    summaryTitle?: string; // Optional title for summary charts

    // Event handlers - all required for proper parent/child communication
    onFieldChange: (field: string) => void;
    onChartTypeChange: (type: ChartType) => void;
    onDateGroupingChange: (grouping: DateGroupingInterval) => void;
    onShowDataTableChange: (show: boolean) => void;
    onDatasetChange: (dataset: string) => void;
    onTimeRangeChange: (range: string) => void;
    onSummaryTitleChange?: (title: string) => void;

    // State
    isLoading?: boolean;
  };
}

export default class ChartingComponent extends Component<ChartingSignature> {
  @service declare store: StoreService;

  // Cache to prevent duplicate queries
  private lastQueryKey = '';
  private lastQueryResult: ChartDataPoint[] = [];

  @tracked isExporting = false;

  // Saved report functionality
  @tracked currentSavedReport?: Report;

  // Only keep getters that add logic beyond simple arg access

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(owner: any, args: ChartingSignature['Args']) {
    super(owner, args);
  }

  chartTypes = [
    { key: 'bar', label: 'Bar Chart' },
    { key: 'horizontalBar', label: 'Horizontal Bar' },
    { key: 'line', label: 'Line Chart' },
    { key: 'pie', label: 'Pie Chart' },
    { key: 'donut', label: 'Donut Chart' },
    { key: 'area', label: 'Area Chart' },
    { key: 'summary', label: 'Summary' },
  ];

  datasetOptions = AVAILABLE_DATASETS.map((dataset) => ({
    key: dataset.key,
    label: dataset.label,
  }));

  timeRangeOptions = TIME_RANGE_OPTIONS;

  // Use date grouping utility for options
  dateGroupingOptions = DateGroupingUtility.getAvailableIntervals();

  // Fetch mapping for the current dataset
  mappingData = trackedFunction(this, async (): Promise<FieldOption[]> => {
    // Capture values before await to avoid this context issues
    const selectedDataset = this.args.selectedDataset;

    try {
      const modelType = this.getModelType();
      const mapping: OpenSearchMapping = await this.store.adapterFor('search').mapping(modelType);

      return this.parseFieldsFromMapping(mapping, modelType);
    } catch (error) {
      console.error('Error fetching mapping:', error);
      return OpenSearchFieldMapper.getFallbackFields(selectedDataset);
    }
  });

  getFallbackFields(): FieldOption[] {
    return OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset);
  }

  get isMappingLoading() {
    return this.mappingData.isLoading;
  }

  get currentFieldInfo() {
    return this.availableFields.find((field) => field.key === this.normalizedSelectedField);
  }

  get isCurrentFieldDate() {
    return this.currentFieldInfo?.isDate || false;
  }

  get isCurrentFieldTimestamp() {
    return this.currentFieldInfo?.isTimestamp || false;
  }

  // Parse fields from OpenSearch mapping using the utility
  parseFieldsFromMapping(mapping: OpenSearchMapping, modelType: string): FieldOption[] {
    const fields = OpenSearchFieldMapper.parseMapping(mapping);

    if (fields.length === 0) {
      console.warn('No mapping properties found for', modelType);
      return OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset);
    }

    return fields;
  }

  // Get available fields for the current dataset
  get availableFields() {
    return (
      this.mappingData.value || OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset)
    );
  }

  /**
   * Normalize selectedField to camelCase format for matching with availableFields
   * Saved reports store fields in snake_case, but the mapping API returns camelCase
   */
  get normalizedSelectedField(): string | undefined {
    if (!this.args.selectedField) {
      return undefined;
    }

    const original = this.args.selectedField;

    // If field contains underscores, convert from snake_case to camelCase
    if (original.includes('_')) {
      return snakeToCamelCase(original);
    }

    // Already in camelCase format
    return original;
  }

  chartData = trackedFunction(this, async (): Promise<ChartDataPoint[]> => {
    // Capture all the values we need BEFORE any await to avoid this context issues
    const selectedDataset = this.args.selectedDataset;
    const selectedGroupByField = this.args.selectedField;
    const selectedTimeRange = this.args.selectedTimeRange;
    const selectedDateGrouping = this.args.dateGrouping;
    const isCurrentFieldDate = this.isCurrentFieldDate;
    const isCurrentFieldTimestamp = this.isCurrentFieldTimestamp;

    // Create a unique key for this query to prevent duplicates
    const queryKey = `${selectedDataset}-${selectedGroupByField}-${selectedTimeRange}-${selectedDateGrouping}-${isCurrentFieldDate}-${isCurrentFieldTimestamp}`;

    // Check if we already have this exact query result
    if (this.lastQueryKey === queryKey && this.lastQueryResult.length > 0) {
      return this.lastQueryResult;
    }

    try {
      const modelType = this.getModelType();
      const builder = this.buildQuery();

      const query = {
        'model-type': modelType,
        'search-body': builder.build(),
      };

      const response = await this.store.adapterFor('search').search(query);
      const responseBuilder = OpenSearchResponseBuilder.from(response);

      const result = ChartDataTransformer.fromOpenSearchResponse(
        responseBuilder,
        selectedGroupByField,
        {
          isDate: isCurrentFieldDate,
          isTimestamp: isCurrentFieldTimestamp,
          dateGrouping: selectedDateGrouping,
          sumField: this.args.sumField,
        }
      );

      // Cache the result
      this.lastQueryKey = queryKey;
      this.lastQueryResult = result;

      return result;
    } catch (error) {
      console.error('Error fetching chart data:', error);
      return [];
    }
  });

  get currentData() {
    return this.chartData.value || [];
  }

  get totalCount() {
    return this.currentData.reduce((sum, dataPoint) => sum + dataPoint.value, 0);
  }

  get currentDataWithPercentages() {
    const total = this.totalCount;
    return this.currentData.map((dataPoint) => ({
      ...dataPoint,
      percentage: total > 0 ? ((dataPoint.value / total) * 100).toFixed(1) : '0.0',
    }));
  }

  get currentDataAsJson() {
    return JSON.stringify(this.currentData, null, 2);
  }

  get isLoading() {
    return this.chartData.isLoading;
  }

  getModelType = () => {
    return getModelTypeFromDataset(this.args.selectedDataset);
  };

  buildQuery(): OpenSearchQueryBuilder {
    const builder = new OpenSearchQueryBuilder();

    // Add time range filter if specified
    const timeFilter = this.getTimeRangeFilter();
    if (timeFilter) {
      builder.addFilter({
        range: {
          [timeFilter.field]: {
            gte: timeFilter.gte,
          },
        },
      });
    }

    // Convert camelCase field names to snake_case for OpenSearch
    const snakeCaseField = camelToSnakeCase(this.args.selectedField);
    const aggKey = `${this.args.selectedField}_agg`;

    // Add aggregations based on field type
    if (this.isCurrentFieldDate) {
      // For date fields, we'll need to add a custom date histogram aggregation
      // Since OpenSearchQueryBuilder doesn't have a direct method for this,
      // we'll add it via the raw aggregation method after building
      builder.addTermsAggregation(aggKey, snakeCaseField, 100);
    } else {
      // For non-date fields, use terms aggregation
      builder.addTermsAggregation(
        aggKey,
        snakeCaseField,
        this.isCurrentFieldTimestamp ? 1000 : 100
      );
    }

    // If sumField is provided, add a nested sum aggregation
    if (this.args.sumField) {
      const snakeCaseSumField = camelToSnakeCase(this.args.sumField);
      const sumAggKey = `${this.args.sumField}_sum`;
      builder.addNestedAggregation(aggKey, sumAggKey, {
        sum: {
          field: snakeCaseSumField,
        },
      });
    }

    return builder;
  }

  // Helper method for date grouping intervals - now using utility
  getCalendarInterval() {
    return DateGroupingUtility.getCalendarInterval(this.args.dateGrouping);
  }

  getDateFormat() {
    return DateGroupingUtility.getDateFormat(this.args.dateGrouping);
  }

  get chartXLabel() {
    if (this.args.chartType === 'summary' && this.args.summaryTitle) return undefined;
    return this.args.selectedDataset === 'people' ? 'Company' : 'Category';
  }

  get chartYLabel() {
    return this.args.selectedDataset === 'people' ? 'Number of People' : 'Count';
  }

  get chartLabel() {
    if (this.args.chartType === 'summary') {
      return this.args.summaryTitle || 'Total';
    }
    return undefined;
  }

  updateChartType = (key: string) => {
    if (key) {
      this.args.onChartTypeChange(key as ChartType);
    }
  };

  updateDataset = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onDatasetChange(key);
    }
  };

  updateTimeRange = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onTimeRangeChange(key);
    }
  };

  updateGroupByField = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onFieldChange(key);
    }
  };

  updateSummaryTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onSummaryTitleChange?.(target.value);
  };

  updateDateGrouping = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onDateGroupingChange(key as DateGroupingInterval);
    }
  };

  toggleTableView = () => {
    // Notify parent component of the change
    this.args.onShowDataTableChange(!this.args.showDataTable);
  };

  clearQueryCache() {
    this.lastQueryKey = '';
    this.lastQueryResult = [];
  }

  getDefaultGroupByField() {
    return OpenSearchFieldMapper.getDefaultField(this.args.selectedDataset);
  }

  // Helper method to get the date field name for time range filtering
  getDateFieldForDataset() {
    switch (this.args.selectedDataset) {
      case 'people':
        return 'updated_at'; // People records use updated_at
      case 'tas-program-instances':
        return 'created_at'; // TAS programs use created_at
      case 'pslf-forms':
        return 'created_at'; // PSLF forms use created_at
      default:
        return 'created_at';
    }
  }

  // Helper method to convert time range to date filter
  getTimeRangeFilter(): { field: string; gte: string } | null {
    // Don't apply time range filter if:
    // 1. No time range selected or "all" is selected
    // 2. We're grouping by a date field (date grouping handles the time aspect)
    if (
      !this.args.selectedTimeRange ||
      this.args.selectedTimeRange === 'all' ||
      this.isCurrentFieldDate
    ) {
      return null;
    }

    const dateField = this.getDateFieldForDataset();
    const now = new Date();
    let startDate: Date;

    // Parse the time range format (e.g., "3m" = 3 months)
    const match = this.args.selectedTimeRange.match(/^(\d+)([myd])$/);
    if (!match || !match[1] || !match[2]) return null;

    const [, amount, unit] = match;
    const amountNum = parseInt(amount, 10);

    switch (unit) {
      case 'm': // months
        startDate = new Date(now.getFullYear(), now.getMonth() - amountNum, now.getDate());
        break;
      case 'y': // years
        startDate = new Date(now.getFullYear() - amountNum, now.getMonth(), now.getDate());
        break;
      case 'd': // days
        startDate = new Date(now.getTime() - amountNum * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return {
      field: dateField,
      gte: startDate.toISOString(),
    };
  }

  exportData = async () => {
    if (this.isExporting) {
      return; // Prevent double-clicks
    }

    this.isExporting = true;

    try {
      // Build the same query we use for charting but for CSV export
      const modelType = this.getModelType();

      // For CSV export, we want the raw data, not aggregated data
      const builder = new OpenSearchQueryBuilder();
      // Don't set size to 0 for CSV - we want actual records

      const query = {
        'model-type': modelType,
        'search-body': builder.build(),
      };

      // Use the search adapter's downloadCsv method
      await this.store.adapterFor('search').downloadCsv(query);
    } catch (_error) {
      // Could add a toast notification here
    } finally {
      this.isExporting = false;
    }
  };

  // Saved search methods - ReportToolbar handles the UI, these handle the logic

  handleSaveReport = (report: Report) => {
    this.currentSavedReport = report;
  };

  handleLoadReport = (report: Report) => {
    // Use the ReportLoader for intelligent report loading
    const context: ReportLoadContext = {
      setDataset: (dataset: string) => {
        this.args.onDatasetChange(dataset);
      },
      setGroupByField: (field: string) => {
        this.args.onFieldChange(field);
      },
      setChartType: (type: ChartType) => {
        this.args.onChartTypeChange(type);
      },
      setTimeRange: (range: string) => {
        this.args.onTimeRangeChange(range);
      },
      setDateGrouping: (grouping: DateGroupingInterval) => {
        this.args.onDateGroupingChange(grouping);
      },
      setShowTableView: (show: boolean) => {
        this.args.onShowDataTableChange(show);
      },
      clearQueryCache: this.clearQueryCache,
      setCurrentReport: this.setCurrentReport,
    };

    ReportLoader.loadReport(report, context).catch((error) => {
      console.error('Error loading report:', error);
    });
  };

  handleDeleteReport = (report: Report) => {
    // Clear current report if it was deleted
    if (this.currentSavedReport?.id === report.id) {
      this.currentSavedReport = undefined;
    }
  };

  // Method to set current report (used by ReportToolbar)
  setCurrentReport = (report: Report) => {
    this.currentSavedReport = report;
  };

  get currentSearchConfig() {
    // Build the OpenSearch query
    const query = this.buildQuery().build();

    // Create URL search params for the current state
    const urlParams = new URLSearchParams();
    urlParams.set('dataset', this.args.selectedDataset);
    urlParams.set('groupBy', this.args.selectedField);
    urlParams.set('chartType', this.args.chartType);
    urlParams.set('timeRange', this.args.selectedTimeRange);
    urlParams.set('dateGrouping', this.args.dateGrouping);

    return {
      urlParams: urlParams.toString(),
      openSearchQuery: query,
      metadata: {
        dataset: this.args.selectedDataset,
        groupByField: this.args.selectedField,
        chartType: this.args.chartType,
        timeRange: this.args.selectedTimeRange,
        dateGrouping: this.args.dateGrouping,
      },
    };
  }

  <template>
    <VStack>
      <Section>
        <:header>OpenSearch Data Visualization</:header>
        <:body>
          <p class="text-gray-600 mb-6">
            Dynamic data visualization using OpenSearch aggregations. Charts automatically adapt to
            show data grouped by any available field from the selected dataset's index mapping.
          </p>

          {{#if this.currentSavedReport}}
            <div
              class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4 mb-6"
            >
              <div class="flex items-center">
                <svg
                  class="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Currently viewing:
                    {{this.currentSavedReport.displayName}}
                  </h4>
                  <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Report details available
                  </p>
                </div>
              </div>
            </div>
          {{/if}}

          <HStack class="mb-6 gap-4">
            <div class="flex-1">
              <Select
                @label="Chart Type"
                @items={{this.chartTypes}}
                @selectedKey={{@chartType}}
                @onAction={{this.updateChartType}}
              />
            </div>
            <div class="flex-1">
              <Select
                @label="Dataset"
                @items={{this.datasetOptions}}
                @selectionMode="single"
                @selectedKey={{@selectedDataset}}
                @onAction={{this.updateDataset}}
              />
            </div>
            <div class="flex-1">
              <Select
                @label="Group By Field"
                @items={{this.availableFields}}
                @selectionMode="single"
                @selectedKey={{this.normalizedSelectedField}}
                @onAction={{this.updateGroupByField}}
                @isLoading={{this.isMappingLoading}}
                @placeholder={{if this.isMappingLoading "Loading fields..." "Select field..."}}
              />
            </div>
            {{#if this.isCurrentFieldDate}}
              <div class="flex-1">
                <Select
                  @label="Date Grouping"
                  @items={{this.dateGroupingOptions}}
                  @selectionMode="single"
                  @selectedKey={{@dateGrouping}}
                  @onAction={{this.updateDateGrouping}}
                />
              </div>
            {{else}}
              <div class="flex-1">
                <Select
                  @label="Time Range"
                  @items={{this.timeRangeOptions}}
                  @selectionMode="single"
                  @selectedKey={{@selectedTimeRange}}
                  @onAction={{this.updateTimeRange}}
                />
              </div>
            {{/if}}
          </HStack>

          {{#if (eq @chartType "summary")}}
            <div class="mb-6">
              <label for="summary-title" class="block text-sm font-medium text-gray-700 mb-2">
                Summary Chart Title
              </label>
              <input
                id="summary-title"
                type="text"
                value={{@summaryTitle}}
                {{on "input" this.updateSummaryTitle}}
                placeholder="Enter chart title..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
              />
            </div>
          {{/if}}

          {{#if this.isLoading}}
            <div class="flex items-center justify-center py-8">
              <div class="text-gray-500">Loading chart data...</div>
            </div>
          {{/if}}
        </:body>
      </Section>

      <Section>
        <:header>
          Chart Visualization
        </:header>
        <:actions>
          <Button @intent="default" @appearance="outlined" {{on "click" this.toggleTableView}}>
            {{#if @showDataTable}}
              Hide Table
            {{else}}
              Show Table
            {{/if}}
          </Button>
        </:actions>
        <:body>
          {{#if this.isLoading}}
            <div
              class="w-full flex justify-center items-center min-h-96 border border-gray-200 rounded-lg"
            >
              <div class="text-gray-500">Loading chart...</div>
            </div>
          {{else if (not this.currentData.length)}}
            <div
              class="w-full flex justify-center items-center min-h-96 border border-gray-200 rounded-lg"
            >
              <div class="text-gray-500">No data available</div>
            </div>
          {{else}}
            <NativeChart
              @data={{this.currentData}}
              @type={{@chartType}}
              @xLabel={{this.chartXLabel}}
              @yLabel={{this.chartYLabel}}
              @label={{this.chartLabel}}
              @height={{384}}
              class="w-full min-h-96 rounded-lg"
            />

            {{#if @showDataTable}}
              <div class="mt-6">
                <h3 class="text-lg font-semibold mb-4 text-gray-900">Chart Data Table</h3>
                {{#if this.isLoading}}
                  <div class="text-center py-4 text-gray-500">Loading table data...</div>
                {{else if (not this.currentData.length)}}
                  <div class="text-center py-4 text-gray-500">No data available</div>
                {{else}}
                  <div class="overflow-x-auto">
                    <Table @isLoading={{false}}>
                      <:thead as |options|>
                        <options.tr>
                          <options.th class="text-left">{{@selectedField}}</options.th>
                          <options.th class="text-right">Count</options.th>
                          <options.th class="text-right">Percentage</options.th>
                        </options.tr>
                      </:thead>
                      <:tbody as |options|>
                        {{#each this.currentDataWithPercentages as |dataPoint|}}
                          <options.tr>
                            <options.td class="font-medium">{{dataPoint.category}}</options.td>
                            <options.td class="text-right">{{dataPoint.value}}</options.td>
                            <options.td class="text-right">{{dataPoint.percentage}}%</options.td>
                          </options.tr>
                        {{/each}}
                      </:tbody>
                    </Table>
                  </div>
                  <div class="mt-2 text-sm text-gray-600">
                    Total:
                    {{this.totalCount}}
                    records
                  </div>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        </:body>
      </Section>

      <Section>
        <:header>
          OpenSearch Data & Query Details
        </:header>
        <:body>
          <div class="bg-gray-100 p-4 rounded-lg mb-4">
            <h3 class="font-semibold mb-2 text-gray-900">Current Dataset:
              {{@selectedDataset}}</h3>
            <h4 class="font-medium mb-2 text-gray-900">Group By Field:
              {{@selectedField}}</h4>
            {{#if this.isCurrentFieldDate}}
              <h4 class="font-medium mb-2 text-orange-600 dark:text-orange-400">
                📅 Date Field Detected - Grouping by:
                {{@dateGrouping}}
              </h4>
            {{/if}}
            <h4 class="font-medium mb-2 text-gray-900">Available Fields ({{this.availableFields.length}}):</h4>
            <div class="text-sm text-gray-600 mb-2">
              {{#each this.availableFields as |field|}}
                <span
                  class="inline-block
                    {{if
                      field.isDate
                      'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
                      'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                    }}
                    px-2 py-1 rounded mr-2 mb-1"
                >
                  {{field.label}}
                  ({{field.type}}){{if field.isDate " 📅"}}
                </span>
              {{/each}}
            </div>
            <h4 class="font-medium mb-2 text-gray-900">Data ({{this.currentData.length}}
              records):</h4>
            <pre
              class="text-sm text-gray-700 overflow-x-auto max-h-64"
            >{{this.currentDataAsJson}}</pre>
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
